import React from 'react';
import { Sun, Clock, Users, TrendingUp } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useTypicalSunnyHours } from '@/hooks/useTypicalSunnyHours';

interface TypicalSunnyHoursProps {
  terraceId: string;
}

export const TypicalSunnyHours: React.FC<TypicalSunnyHoursProps> = ({ terraceId }) => {
  const { mostCommonHours, totalReports, recentReports, loading, error } = useTypicalSunnyHours(terraceId);

  if (loading) {
    return (
      <Card className="bg-warm-cream/50 border-peach-orange/20">
        <CardContent className="p-4">
          <div className="flex items-center mb-3">
            <Sun className="w-4 h-4 mr-2 text-peach-orange animate-pulse" />
            <h3 className="font-medium text-sunset-dark">Typical Sunny Hours</h3>
          </div>
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
            <div className="h-3 bg-gray-200 rounded w-1/2"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return null; // Silently fail to not disrupt the page
  }

  return (
    <Card className="bg-warm-cream/50 border-peach-orange/20">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center">
            <Sun className="w-4 h-4 mr-2 text-peach-orange" />
            <h3 className="font-medium text-sunset-dark">Typical Sunny Hours</h3>
          </div>
          {totalReports > 0 && (
            <Badge variant="secondary" className="text-xs">
              <Users className="w-3 h-3 mr-1" />
              {totalReports} reports
            </Badge>
          )}
        </div>

        {mostCommonHours ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-sunset-medium" />
                <span className="font-medium text-sunset-dark">
                  {mostCommonHours.fromTime} - {mostCommonHours.toTime}
                </span>
              </div>
              <div className="flex items-center gap-1 text-xs text-sunset-medium">
                <TrendingUp className="w-3 h-3" />
                {mostCommonHours.frequency}x reported
              </div>
            </div>

            {recentReports > 0 && (
              <p className="text-xs text-sunset-medium">
                {recentReports} recent report{recentReports !== 1 ? 's' : ''} in the last 30 days
              </p>
            )}

            <div className="pt-2 border-t border-peach-orange/10">
              <p className="text-xs text-sunset-medium leading-relaxed">
                💡 <strong>Help improve this data!</strong> Visit during sunny hours and report when the terrace gets sun. 
                Your contributions help other visitors plan their perfect terrace moment.
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="text-center py-4">
              <Sun className="w-8 h-8 mx-auto mb-2 text-sunset-medium/50" />
              <p className="text-sm text-sunset-medium font-medium">No sunny hours reported yet</p>
            </div>
            
            <div className="pt-2 border-t border-peach-orange/10">
              <p className="text-xs text-sunset-medium leading-relaxed">
                🌞 <strong>Be the first to report!</strong> Visit this terrace and share when it gets sun. 
                Check in below to help build valuable sunny hours data for future visitors.
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};